import { useMemo } from 'react'
import {
  canonicalizeReportColumnValue,
  isDefined,
  isNotDefined,
  isNotEmpty,
  uniqueEntities,
  type ReportColumnDefinition,
  type ReportColumnDisplay,
  type ReportKpiGrouping,
  type ReportKpiInput,
  type ReportKpiSuccess,
  type ReportKpiUserFilter,
  type TabularResult,
  type WorkflowId,
} from '@terros/common'
import { useReportKpiV2 } from '../kpi'
import { useReportColumns } from '../column'
import { usePreference } from '../../preferences'
import { type GetHookResponse } from '../../hooks'
import { type ServerReportFilter } from './types'

export type WithStoredColumns = {
  storedColumns: string[]
}

export type ServerReportState = GetHookResponse<ReportKpiSuccess> & {
  visibleColumns: ReportColumnDefinition[]
  reportInput: ReportKpiInput
}

export type ResolvedServerReportState = Omit<ServerReportState, 'data'> & {
  data: TabularResult
}

export type ServerReportStateProps = {
  filter: ServerReportFilter
  columns: ServerReportColumn[]
  hideSystemUsers?: boolean
  showArchived?: boolean
  includeUsersWithNoData?: ReportKpiUserFilter
  skipReportColumnsCheck?: boolean
  workflowId?: WorkflowId
  skipKpiFetch?: boolean
}

export type ServerReportColumn = string | ReportColumnDisplay | ReportColumnDefinition

export const useServerReportState = ({
  filter,
  columns,
  hideSystemUsers = false,
  showArchived,
  includeUsersWithNoData,
  skipReportColumnsCheck = false,
  workflowId,
  skipKpiFetch = false,
}: ServerReportStateProps): ServerReportState => {
  const {
    data,
    isLoading: isLoadingColumns,
    refetch: refetchColumns,
    isFetching: isFetchingColumns,
  } = useReportColumns({ workflowId }, !skipReportColumnsCheck)
  const { data: managerBasedTeams } = usePreference('managerBasedTeams')

  const visibleColumnDefinitions = useMemo(() => {
    if (skipReportColumnsCheck) return []
    const resolvedColumns = getColumns(columns.map(toColumnValue), data)
    return toVisibleColumns(data ?? [], resolvedColumns)
  }, [columns, data, skipReportColumnsCheck])

  const reportFilter = useMemo(() => {
    return convertFilter({
      filter,
      visibleColumns: visibleColumnDefinitions,
      columns,
      hideSystemUsers,
      showArchived,
      includeUsersWithNoData,
      skipReportColumnsCheck: skipReportColumnsCheck,
      managerBasedTeams,
      workflowId,
    })
  }, [
    filter,
    visibleColumnDefinitions,
    columns,
    hideSystemUsers,
    showArchived,
    includeUsersWithNoData,
    skipReportColumnsCheck,
    managerBasedTeams,
    workflowId,
  ])
  const state = useReportKpiV2(reportFilter, !skipKpiFetch)

  return {
    ...state,
    isLoading: state.isLoading || isLoadingColumns,
    isFetching: state.isFetching || isFetchingColumns,
    refetch: () => {
      state.refetch()
      refetchColumns()
    },
    visibleColumns: skipReportColumnsCheck ? [] : visibleColumnDefinitions,
    reportInput: reportFilter,
  }
}

type ConverFilterParams = {
  filter: ServerReportFilter | undefined
  visibleColumns: ReportColumnDefinition[]
  columns: ServerReportColumn[]
  hideSystemUsers: boolean
  showArchived?: boolean
  includeUsersWithNoData?: ReportKpiUserFilter
  skipReportColumnsCheck: boolean
  managerBasedTeams?: boolean
  workflowId?: WorkflowId
}

const convertFilter = ({
  filter,
  visibleColumns,
  columns,
  hideSystemUsers,
  showArchived,
  includeUsersWithNoData,
  skipReportColumnsCheck,
  managerBasedTeams,
  workflowId,
}: ConverFilterParams): ReportKpiInput => {
  if (filter === undefined) return { kpis: [], grouping: { type: 'user' } }
  const { start, end } = filter
  const teamIds = filter.teamIds
  const grouping: ReportKpiGrouping = managerBasedTeams ? { type: 'user' } : filter.grouping
  return {
    grouping,
    filter: {
      startDate: start,
      endDate: end,
      teams: teamIds ?? [],
      workflowId,
      programUserStatus: filter.programUserStatus,
      programUserStartDate: filter.programUserStartDate,
    },
    includeUsersWithNoData,
    kpis: skipReportColumnsCheck ? columns.map(toReportColumnInput) : visibleColumns.map(toReportColumnDisplay),
    hideSystemUsers,
    showArchived,
  }
}

const getColumns = (stored: string[], loaded: ReportColumnDefinition[] | undefined): string[] => {
  if (isNotEmpty(stored)) return stored
  if (isNotEmpty(loaded)) return loaded.filter((c) => c.default).map((c) => c.value)
  return []
}

export const toVisibleColumns = (columns: ReportColumnDefinition[], selected: string[]): ReportColumnDefinition[] => {
  const resolved = selected.map((value) => findReportColumn(columns, value)).filter(isDefined)
  return uniqueEntities(resolved, (column) => column.value)
}

function findReportColumn(columns: ReportColumnDefinition[], value: string): ReportColumnDefinition | undefined {
  const canonical = canonicalizeReportColumnValue(value, columns)
  if (isNotDefined(canonical)) return
  return columns.find((column) => column.value === canonical)
}

const toReportColumnDisplay = (col: ReportColumnDefinition | string): ReportColumnDisplay =>
  typeof col === 'string' ? { value: col } : { value: col.value, accumulator: col.accumulator }

const toColumnValue = (col: ServerReportColumn): string => (typeof col === 'string' ? col : col.value)

const isReportColumnDefinition = (col: ServerReportColumn): col is ReportColumnDefinition => {
  if (typeof col === 'string') return false
  return 'label' in col
}

const toReportColumnInput = (col: ServerReportColumn): ReportColumnDisplay => {
  if (typeof col === 'string') return { value: col }
  if (isReportColumnDefinition(col)) return toReportColumnDisplay(col)
  return col
}
